import React, { useState, useEffect, useRef, useCallback } from "react";
import { Play, ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

export default function CarGallery({ images = [], title, video = null }) {
  // Unified media: native Shopify video (if present) first, then images.
  const slides = [
    ...(video ? [{ kind: "video", ...video }] : []),
    ...images.map((src) => ({ kind: "image", src })),
  ];
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const touchX = useRef(null);
  const thumbsRef = useRef(null);
  const total = slides.length;

  const go = useCallback(
    (dir) => setActive((i) => (i + dir + total) % total),
    [total]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go]);

  useEffect(() => {
    const el = thumbsRef.current?.querySelector(`[data-idx="${active}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [active]);

  if (!total) return null;
  const current = slides[active];
  const isVideo = current.kind === "video";
  const posterSrc = video?.poster || images[0];

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  const Arrow = ({ dir, testid }) => (
    <button
      type="button"
      data-testid={testid}
      onClick={(e) => { e.stopPropagation(); go(dir); }}
      aria-label={dir < 0 ? "Media precedente" : "Media successivo"}
      className="absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[var(--cm-ink)] shadow-md ring-1 ring-black/5 transition-colors hover:bg-white"
      style={dir < 0 ? { left: 10 } : { right: 10 }}
    >
      {dir < 0 ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
    </button>
  );

  const VideoPlayer = ({ v, className }) => {
    const [started, setStarted] = useState(false);
    const ref = useRef(null);
    const poster = v.poster || images[0];

    if (v.type === "external") {
      if (!started) {
        return (
          <button
            type="button"
            onClick={() => setStarted(true)}
            aria-label="Riproduci video"
            data-testid="gallery-video-play"
            className="group relative block h-full w-full"
          >
            <img src={poster} alt={title} className="h-full w-full object-cover" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/35">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-lg ring-1 ring-black/5 transition-transform group-hover:scale-105">
                <Play size={26} className="ml-1 fill-[var(--cm-ink)] text-[var(--cm-ink)]" />
              </span>
            </span>
          </button>
        );
      }
      const src = v.url + (v.url.includes("?") ? "&" : "?") + "autoplay=1&mute=1";
      return (
        <iframe
          src={src}
          title={title || "Video veicolo"}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className={className}
          data-testid="gallery-video-player"
        />
      );
    }

    return (
      <div className="relative h-full w-full">
        <video
          ref={ref}
          src={v.url}
          poster={poster}
          controls={started}
          playsInline
          preload="metadata"
          onPlay={() => setStarted(true)}
          className={className}
          data-testid="gallery-video-player"
        />
        {!started && (
          <button
            type="button"
            onClick={() => {
              setStarted(true);
              const el = ref.current;
              if (el) {
                const p = el.play();
                if (p && p.catch) p.catch(() => { el.muted = true; el.play().catch(() => {}); });
              }
            }}
            aria-label="Riproduci video"
            data-testid="gallery-video-play"
            className="group absolute inset-0 flex items-center justify-center bg-black/20 transition-colors hover:bg-black/30"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-lg ring-1 ring-black/5 transition-transform group-hover:scale-105">
              <Play size={26} className="ml-1 fill-[var(--cm-ink)] text-[var(--cm-ink)]" />
            </span>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="min-w-0">
      <div className="relative">
        <div
          className="relative w-full overflow-hidden rounded-[18px] border border-[var(--cm-line)] bg-black"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {isVideo ? (
            <div className="aspect-[4/3]">
              <VideoPlayer v={current} className="h-full w-full bg-black object-contain" />
            </div>
          ) : (
            <button
              type="button"
              data-testid="gallery-open-lightbox"
              onClick={() => setOpen(true)}
              className="block w-full cursor-zoom-in bg-white text-left"
              aria-label="Ingrandisci foto"
            >
              <div className="aspect-[4/3]">
                <img src={current.src} alt={title} className="h-full w-full object-cover" />
              </div>
            </button>
          )}

          {total > 1 && <Arrow dir={-1} testid="gallery-prev" />}
          {total > 1 && <Arrow dir={1} testid="gallery-next" />}

          <span className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 font-mono-cm text-[0.6rem] uppercase tracking-[0.12em] text-white backdrop-blur">
            {isVideo ? <Play size={11} /> : <ZoomIn size={11} />} {active + 1} / {total}
          </span>
        </div>
      </div>

      {/* Compact, swipeable thumbnail strip */}
      <div ref={thumbsRef} className="cm-thumbs mt-3 flex gap-2 overflow-x-auto pb-1">
        {slides.map((sl, i) => (
          <button
            key={(sl.src || sl.url || "v") + i}
            data-idx={i}
            data-testid={`gallery-thumb-${i}`}
            onClick={() => setActive(i)}
            className={`relative h-14 w-[74px] shrink-0 overflow-hidden rounded-[9px] border transition-all sm:h-16 sm:w-20 ${
              active === i
                ? "border-[var(--cm-ink)] ring-1 ring-[var(--cm-ink)]"
                : "border-[var(--cm-line)] opacity-70 hover:opacity-100"
            }`}
          >
            <img src={sl.kind === "video" ? posterSrc : sl.src} alt="" loading="lazy" className="h-full w-full object-cover" />
            {sl.kind === "video" && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/35">
                <Play size={16} className="fill-white text-white" />
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Fullscreen image lightbox (images only) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-[1180px] border-none bg-transparent p-0 shadow-none [&>button]:hidden">
          <DialogTitle className="sr-only">{title || "Foto veicolo"}</DialogTitle>
          <div className="relative" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {!isVideo && (
              <img src={current.src} alt={title} className="max-h-[86vh] w-full rounded-[14px] object-contain" />
            )}
            <button
              type="button"
              data-testid="gallery-close"
              onClick={() => setOpen(false)}
              aria-label="Chiudi"
              className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[var(--cm-ink)] shadow-md ring-1 ring-black/5 transition-colors hover:bg-white"
            >
              <X size={18} />
            </button>
            {total > 1 && <Arrow dir={-1} testid="gallery-lightbox-prev" />}
            {total > 1 && <Arrow dir={1} testid="gallery-lightbox-next" />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
